import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer"

export type CoversheetData = {
  // To fields — auto-populated from selected recipient
  toName: string
  toCompany: string
  toPhone: string
  toFax: string
  // From fields
  fromName: string
  fromCompany: string
  fromPhone: string
  fromFax: string
  // Details
  date: string
  pages: string
  subject: string
  priority: "urgent" | "review" | "reply" | "comment"
  message: string
  // Logo (base64 data URL or empty)
  logoUrl: string
}

const styles = StyleSheet.create({
  page: {
    padding: 48,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: "#1a1a1a",
  },
  header: {
    borderBottomWidth: 3,
    borderBottomColor: "#1a1a1a",
    paddingBottom: 12,
    marginBottom: 20,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  headerLeft: {},
  logo: {
    width: 80,
    height: 80,
    objectFit: "contain",
  },
  title: {
    fontSize: 28,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 2,
    textTransform: "uppercase",
  },
  subtitle: {
    fontSize: 10,
    color: "#666",
    marginTop: 4,
  },
  row: {
    flexDirection: "row",
    marginBottom: 0,
  },
  column: {
    flex: 1,
  },
  sectionTitle: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#666",
    marginBottom: 8,
    marginTop: 16,
  },
  fieldRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#ddd",
    paddingVertical: 5,
  },
  fieldLabel: {
    width: 70,
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: "#444",
  },
  fieldValue: {
    flex: 1,
    fontSize: 10,
  },
  priorityRow: {
    flexDirection: "row",
    gap: 16,
    marginTop: 16,
    marginBottom: 16,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: "#f5f5f5",
    borderRadius: 4,
  },
  priorityItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  priorityBox: {
    width: 10,
    height: 10,
    borderWidth: 1,
    borderColor: "#999",
  },
  priorityBoxChecked: {
    width: 10,
    height: 10,
    borderWidth: 1,
    borderColor: "#1a1a1a",
    backgroundColor: "#1a1a1a",
  },
  priorityLabel: {
    fontSize: 9,
  },
  messageSection: {
    marginTop: 16,
  },
  messageTitle: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#666",
    marginBottom: 8,
  },
  messageBox: {
    borderWidth: 0.5,
    borderColor: "#ddd",
    padding: 12,
    minHeight: 160,
    borderRadius: 2,
  },
  messageText: {
    fontSize: 10,
    lineHeight: 1.6,
  },
  footer: {
    position: "absolute",
    bottom: 36,
    left: 48,
    right: 48,
    borderTopWidth: 0.5,
    borderTopColor: "#ddd",
    paddingTop: 8,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  footerText: {
    fontSize: 8,
    color: "#999",
  },
})

const priorities = ["urgent", "review", "reply", "comment"] as const

export function CoversheetDocument({ data }: { data: CoversheetData }) {
  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.title}>Fax</Text>
            <Text style={styles.subtitle}>Cover Sheet</Text>
          </View>
          {data.logoUrl ? (
            <Image src={data.logoUrl} style={styles.logo} />
          ) : null}
        </View>

        {/* Two-column: To and From */}
        <View style={styles.row}>
          {/* To Column */}
          <View style={styles.column}>
            <Text style={styles.sectionTitle}>To</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Name</Text>
              <Text style={styles.fieldValue}>
                {data.toName || "\u2014"}
              </Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Company</Text>
              <Text style={styles.fieldValue}>
                {data.toCompany || "\u2014"}
              </Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Phone</Text>
              <Text style={styles.fieldValue}>
                {data.toPhone || "\u2014"}
              </Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Fax</Text>
              <Text style={styles.fieldValue}>
                {data.toFax || "\u2014"}
              </Text>
            </View>
          </View>

          {/* Spacer */}
          <View style={{ width: 24 }} />

          {/* From Column */}
          <View style={styles.column}>
            <Text style={styles.sectionTitle}>From</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Name</Text>
              <Text style={styles.fieldValue}>
                {data.fromName || "\u2014"}
              </Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Company</Text>
              <Text style={styles.fieldValue}>
                {data.fromCompany || "\u2014"}
              </Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Phone</Text>
              <Text style={styles.fieldValue}>
                {data.fromPhone || "\u2014"}
              </Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Fax</Text>
              <Text style={styles.fieldValue}>
                {data.fromFax || "\u2014"}
              </Text>
            </View>
          </View>
        </View>

        {/* Date / Pages / Subject */}
        <View style={{ marginTop: 8 }}>
          <Text style={styles.sectionTitle}>Details</Text>
          <View style={styles.fieldRow}>
            <Text style={styles.fieldLabel}>Date</Text>
            <Text style={styles.fieldValue}>{data.date || "\u2014"}</Text>
          </View>
          <View style={styles.fieldRow}>
            <Text style={styles.fieldLabel}>Pages</Text>
            <Text style={styles.fieldValue}>
              {data.pages || "\u2014"} (including cover sheet)
            </Text>
          </View>
          <View style={styles.fieldRow}>
            <Text style={styles.fieldLabel}>Subject</Text>
            <Text style={styles.fieldValue}>
              {data.subject || "\u2014"}
            </Text>
          </View>
        </View>

        {/* Priority */}
        <View style={styles.priorityRow}>
          {priorities.map((p) => (
            <View key={p} style={styles.priorityItem}>
              <View
                style={
                  data.priority === p
                    ? styles.priorityBoxChecked
                    : styles.priorityBox
                }
              />
              <Text style={styles.priorityLabel}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </Text>
            </View>
          ))}
        </View>

        {/* Message */}
        <View style={styles.messageSection}>
          <Text style={styles.messageTitle}>Notes / Comments</Text>
          <View style={styles.messageBox}>
            <Text style={styles.messageText}>
              {data.message || ""}
            </Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>Confidential</Text>
          <Text style={styles.footerText}>Page 1 of 1</Text>
        </View>
      </Page>
    </Document>
  )
}
